import React from "react";
import { useLang } from "@/i18n/LanguageContext";
import { Plane, PlaneTakeoff, PlaneLanding, ArrowRight } from "lucide-react";

const OUTBOUND = {
  key: "outbound",
  dateRange: "October 10–11, 2026",
  legs: [
    { date: "Saturday, October 10", flight: "MS 996", fromCode: "YYZ", fromCity: "Toronto", toCode: "CAI", toCity: "Cairo", depart: "12:45 PM", departNote: "Oct 10", arrive: "6:10 AM", arriveNote: "Oct 11", nextDay: true },
    { date: "Sunday, October 11", flight: "MS 695", fromCode: "CAI", fromCity: "Cairo", toCode: "MED", toCity: "Madinah", depart: "7:50 AM", departNote: "", arrive: "9:40 AM", arriveNote: "", nextDay: false },
  ],
};

const RETURN = {
  key: "return",
  dateRange: "October 21–22, 2026",
  legs: [
    { date: "Wednesday, October 21", flight: "MS 670", fromCode: "JED", fromCity: "Jeddah", toCode: "CAI", toCity: "Cairo", depart: "11:00 PM", departNote: "Oct 21", arrive: "1:10 AM", arriveNote: "Oct 22", nextDay: true },
    { date: "Thursday, October 22", flight: "MS 995", fromCode: "CAI", fromCity: "Cairo", toCode: "YYZ", toCity: "Toronto", depart: "2:55 AM", departNote: "", arrive: "7:25 AM", arriveNote: "", nextDay: false },
  ],
};

const Endpoint = ({ label, time, note, code, city, nextDay, nextDayLabel }) => (
  <div className="min-w-0 flex-1">
    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</p>
    <p className="mt-0.5 flex flex-wrap items-baseline gap-x-1.5">
      <span className="font-display text-2xl font-bold text-[#0A192F]">{time}</span>
      {nextDay && (
        <span className="rounded-full bg-[#EAF2FC] px-2 py-0.5 text-[10px] font-bold text-[#3E6B9A]">
          +1 · {nextDayLabel}
        </span>
      )}
    </p>
    <p className="mt-0.5 text-sm font-semibold text-[#0A192F]">
      {code} <span className="font-normal text-slate-500">· {city}</span>
    </p>
    {note && <p className="text-[11px] text-slate-400">{note}</p>}
  </div>
);

const FlightLeg = ({ leg }) => {
  const { t } = useLang();
  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-4" data-testid={`flight-leg-${leg.flight.replace(/\s/g, "")}`}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-slate-500">{leg.date}</span>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#0A192F] px-2.5 py-1 text-[11px] font-bold text-[#E9CE7D]">
          <Plane size={12} /> EgyptAir {leg.flight}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <Endpoint label={t("flights.depart")} time={leg.depart} note={leg.departNote} code={leg.fromCode} city={leg.fromCity} />
        <div className="flex shrink-0 flex-col items-center px-1 text-[#B9D0EC]">
          <PlaneTakeoff size={16} />
          <ArrowRight size={16} className="my-0.5 text-slate-300" />
          <PlaneLanding size={16} />
        </div>
        <Endpoint label={t("flights.arrive")} time={leg.arrive} note={leg.arriveNote} code={leg.toCode} city={leg.toCity} nextDay={leg.nextDay} nextDayLabel={t("flights.nextDay")} />
      </div>
    </div>
  );
};

const FlightGroup = ({ group, title, tone }) => (
  <div className="maqam-card overflow-hidden p-0" data-testid={`flight-group-${group.key}`}>
    <div className={`flex items-center gap-3 px-5 py-4 ${tone}`}>
      <span className="grid h-10 w-10 place-items-center rounded-xl bg-white/15 text-white">
        <Plane size={18} strokeWidth={1.8} />
      </span>
      <div>
        <p className="font-display text-lg font-bold leading-tight text-white">{title}</p>
        <p className="text-xs text-white/70">{group.dateRange}</p>
      </div>
    </div>
    <div className="space-y-3 p-4">
      {group.legs.map((leg, i) => (
        <FlightLeg key={i} leg={leg} />
      ))}
    </div>
  </div>
);

export const FlightItinerary = () => {
  const { t } = useLang();
  return (
    <div className="space-y-5" data-testid="flight-itinerary">
      <FlightGroup group={OUTBOUND} title={t("flights.outbound")} tone="bg-[#0A192F]" />
      <FlightGroup group={RETURN} title={t("flights.return")} tone="bg-[#112B45]" />
      <p className="text-center text-xs text-slate-400">{t("flights.localTimes")}</p>
    </div>
  );
};
